import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, KPICard, ConfirmDialog } from '@/components/ui';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

// Internal
import { BoqImportUploader } from './components/BoqImportUploader';
import { BoqImportPreviewTable } from './components/BoqImportPreviewTable';
import { useBoqImport } from './hooks/useBoqImport';
import { useConstructionBoqDetail } from './hooks/useConstructionBoqDetail';

export default function BoqImportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  // Get BOQ ID from query params or state
  const queryParams = new URLSearchParams(location.search);
  const boqId = queryParams.get('boqId') || '';

  const { boq, isLoading: isBoqLoading } = useConstructionBoqDetail(boqId);
  const { importItems, isImporting } = useBoqImport(boqId);

  const [step, setStep] = useState(1);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleFileSelect = async (file: File) => {
    // Mock parsing file
    toast.info('Đang xử lý file: ' + file.name);

    // Simulate parsing delay
    setTimeout(() => {
      const mockData = [
        { item_code: 'CV-01', item_name: 'San lấp mặt bằng', unit: 'm2', quantity: 1500, unit_price: 45000, total_amount: 67500000, description: 'Độ dày trung bình 30cm' },
        { item_code: 'CV-02', item_name: 'Trồng cỏ nhung nhật', unit: 'm2', quantity: 1200, unit_price: 85000, total_amount: 102000000, description: 'Bao gồm phân bón lót' },
        { item_code: 'VT-01', item_name: 'Cây lộc vừng (D20-25)', unit: 'Cây', quantity: 15, unit_price: 4500000, total_amount: 67500000, description: 'Cây đánh bầu, tán tròn' },
        { item_code: 'VT-02', item_name: 'Cây tùng la hán (H2.5m)', unit: 'Cây', quantity: 4, unit_price: 12000000, total_amount: 48000000, description: 'Dáng trực, tay cành đều' },
        { item_code: 'HT-01', item_name: 'Hệ thống tưới tự động', unit: 'Gói', quantity: 1, unit_price: 150000000, total_amount: 150000000, description: 'Thiết bị Hunter, USA' },
      ];
      setPreviewData(mockData);
      setStep(2);
      toast.success('Xử lý file hoàn tất. Vui lòng kiểm tra lại dữ liệu.');
    }, 1500);
  };

  const handleConfirmImport = async () => {
    try {
      await importItems(previewData);
      setShowConfirm(false);
      navigate(`/construction/boq/detail/${boqId}`);
    } catch (error) {
      // Error handled in hook
    }
  };

  const totalValue = previewData.reduce((sum, item) => sum + item.total_amount, 0);

  return (
    <PageLayout
      title="Import Dữ liệu BOQ"
      description={boq ? `Tải danh sách hạng mục cho bảng: ${boq.boq_name}` : 'Tải danh sách hạng mục từ file Excel vào bảng khối lượng.'}
      breadcrumbs={[
        { label: 'Thi công' },
        { label: 'BOQ', path: '/construction/boq' },
        { label: 'Nhập từ Excel' }
      ]}
      isLoading={isBoqLoading}
    >
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Step Indicator */}
        <div className="flex items-center justify-between relative px-8">
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-1/2 -z-10" />
          {[1, 2].map((s) => (
            <div key={s} className="flex flex-col items-center gap-2 bg-white px-4">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all
                ${step >= s ? 'bg-os-primary text-white shadow-lg shadow-os-primary/20' : 'bg-slate-100 text-slate-400'}
              `}>
                {step > s ? <i className="ri-check-line" /> : s}
              </div>
              <span className={`text-xs font-bold  tracking-wider ${step >= s ? 'text-os-primary' : 'text-slate-400'}`}>
                {s === 1 ? 'Tải lên file' : 'Xác nhận dữ liệu'}
              </span>
            </div>
          ))}
        </div>

        {step === 1 ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <BoqImportUploader onFileSelect={handleFileSelect} />
          </div>
        ) : (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <KPICard
                title="Tổng giá trị import"
                value={totalValue}
                format="compact-currency"
                suffix="VNĐ"
                variant="primary"
                icon="ri-money-dollar-circle-line"
              />
              <KPICard
                title="Số lượng hạng mục"
                value={previewData.length}
                variant="info"
                icon="ri-list-check"
              />
              <KPICard
                title="Tỷ lệ chính xác"
                value={100}
                format="percentage"
                variant="success"
                icon="ri-shield-check-line"
              />
            </div>

            <BoqImportPreviewTable data={previewData} />

            <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
              <Button variant="ghost" icon="ri-arrow-left-line" onClick={() => setStep(1)}>
                Làm lại / Chọn file khác
              </Button>
              <Button
                variant="primary"
                icon="ri-checkbox-circle-line"
                size="lg"
                onClick={() => setShowConfirm(true)}
                isLoading={isImporting}
              >
                Xác nhận Import {previewData.length} hạng mục
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Xác nhận Import BOQ"
        message={`Bạn đang chuẩn bị import ${previewData.length} hạng mục vào bảng khối lượng "${boq?.boq_name}". Hành động này sẽ thêm mới các dòng vào bảng hiện tại.`}
        confirmText="Bắt đầu Import"
        onConfirm={handleConfirmImport}
        onClose={() => setShowConfirm(false)}
        isLoading={isImporting}
      />
    </PageLayout>
  );
}
